import type { WorldTheme } from './theme';

/**
 * World 3 · GUADALAJARA theme (GDD §10.5, §14.2, issue #114). Desert ochre, agave blue and red-orange
 * sunsets with Mexican-pink accents, in three time-of-day variants: `manana` (OP-301), `tarde` (OP-302) and
 * `atardecer` (OP-303). Game art, not design system tokens (GDD §14.1, §17-3). The definite values are
 * recorded in GDD §14.2.
 *
 * Legibility (GDD §14.1): the collidable tints (columns and pickups) stay more saturated than every
 * decoration colour, so nothing that hurts ever looks like scenery, even against a red sunset sky.
 */

const DECORATION = {
  // Morning: pale ochre desert with hazy agave blue.
  mananaFar: 0xa8905e,
  mananaFarSoft: 0xbba672,
  mananaHill: 0x7f8f5a,
  mananaHillDark: 0x6b7a4a,
  mananaTown: 0xa8734a,
  mananaTownDark: 0x8f5f3d,
  mananaForeground: 0x4a6a8a,
  // Afternoon: warm ochre with deeper agave.
  tardeFar: 0xc9a86a,
  tardeFarSoft: 0xd9b877,
  tardeHill: 0x8f9f63,
  tardeHillDark: 0x75855a,
  tardeTown: 0xb57f52,
  tardeTownDark: 0x96663f,
  tardeForeground: 0x3a5f80,
  // Sunset: dark red-brown desert against a red-orange sky.
  atardecerFar: 0x7a4a3f,
  atardecerFarSoft: 0x8a5a4a,
  atardecerHill: 0x6b6f4a,
  atardecerHillDark: 0x565a3a,
  atardecerTown: 0x8f5f4a,
  atardecerTownDark: 0x754a3a,
  atardecerForeground: 0x4a3a5a,
} as const;

const layers = (palette: {
  far: readonly [number, number];
  hill: readonly [number, number];
  town: readonly [number, number];
  foreground: number;
}): WorldTheme['layers'] => [
  { name: 'layer.far', colors: palette.far, moduleWidth: 24, height: 8, baseY: 1, motif: 'hills' },
  { name: 'layer.mid', colors: [...palette.hill, ...palette.town], moduleWidth: 15, height: 5, baseY: 1, motif: 'buildings' },
  { name: 'layer.foreground', colors: [palette.foreground], moduleWidth: 10, height: 1.4, baseY: -0.6, motif: 'foreground', followCamera: true },
];

export const WORLD_3_MANANA: WorldTheme = {
  id: 'w3-guadalajara',
  variant: 'manana',
  sky: 0xc9a86a,
  fog: { color: 0xbfa06a, near: 40, far: 150 },
  ground: 0x8f9f63,
  ceiling: 0xe6dcc2,
  collidable: { column: 0x1f8a2e, pickup: 0xe0a000 },
  lights: {
    hemisphere: { sky: 0xfff0d0, ground: 0x5a5a3a, intensity: 1.0 },
    sun: { color: 0xfff2d8, intensity: 1.8, position: [4, 18, 12] },
  },
  layers: layers({
    far: [DECORATION.mananaFar, DECORATION.mananaFarSoft],
    hill: [DECORATION.mananaHill, DECORATION.mananaHillDark],
    town: [DECORATION.mananaTown, DECORATION.mananaTownDark],
    foreground: DECORATION.mananaForeground,
  }),
};

export const WORLD_3_TARDE: WorldTheme = {
  id: 'w3-guadalajara',
  variant: 'tarde',
  sky: 0xd9b877,
  fog: { color: 0xcfae72, near: 40, far: 150 },
  ground: 0x8f9f63,
  ceiling: 0xe8dcc0,
  collidable: { column: 0x1f8a2e, pickup: 0xe0a000 },
  lights: {
    hemisphere: { sky: 0xffe6b8, ground: 0x60502f, intensity: 1.1 },
    sun: { color: 0xffdca0, intensity: 2.0, position: [-2, 16, 12] },
  },
  layers: layers({
    far: [DECORATION.tardeFar, DECORATION.tardeFarSoft],
    hill: [DECORATION.tardeHill, DECORATION.tardeHillDark],
    town: [DECORATION.tardeTown, DECORATION.tardeTownDark],
    foreground: DECORATION.tardeForeground,
  }),
};

export const WORLD_3_ATARDECER: WorldTheme = {
  id: 'w3-guadalajara',
  variant: 'atardecer',
  // A deep wine-red sunset sky. The collidable tints go brighter than in the day variants so they keep
  // the 3:1 contrast of GDD §14.1 against the darkest sky of the world (#114).
  sky: 0x9a2f2a,
  fog: { color: 0x7a2822, near: 40, far: 150 },
  ground: 0x6b6f4a,
  ceiling: 0xe6b48c,
  collidable: { column: 0x33c04a, pickup: 0xf2b000 },
  lights: {
    hemisphere: { sky: 0xffb98a, ground: 0x4a2f28, intensity: 1.0 },
    sun: { color: 0xff9a5a, intensity: 2.1, position: [-8, 8, 12] },
  },
  layers: layers({
    far: [DECORATION.atardecerFar, DECORATION.atardecerFarSoft],
    hill: [DECORATION.atardecerHill, DECORATION.atardecerHillDark],
    town: [DECORATION.atardecerTown, DECORATION.atardecerTownDark],
    foreground: DECORATION.atardecerForeground,
  }),
};

export const WORLD_3: Readonly<Record<string, WorldTheme>> = {
  manana: WORLD_3_MANANA,
  tarde: WORLD_3_TARDE,
  atardecer: WORLD_3_ATARDECER,
};
